// Angular components
import {ErrorHandler, NgModule, OnInit} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {CommonModule} from '@angular/common';

// Primefaces
import {MessageService, PrimeNGConfig} from 'primeng/api';
import {PanelModule} from 'primeng/panel';
import {ToastModule} from 'primeng/toast';
import {MenuModule} from 'primeng/menu';
import {TabViewModule} from 'primeng/tabview';
import {CardModule} from 'primeng/card';
import {ButtonModule} from 'primeng/button';
import {InputMaskModule} from 'primeng/inputmask';
import {InputTextModule} from 'primeng/inputtext';
import {DividerModule} from 'primeng/divider';
import {TableModule} from 'primeng/table';
import {CalendarModule} from 'primeng/calendar';
import {DialogModule} from 'primeng/dialog';
import {DropdownModule} from 'primeng/dropdown';
import {OverlayPanelModule} from 'primeng/overlaypanel';
import {RippleModule} from 'primeng/ripple';
import {MessagesModule} from 'primeng/messages';
import {MessageModule} from 'primeng/message';
import {FieldsetModule} from 'primeng/fieldset';
import {CheckboxModule} from 'primeng/checkbox';
import {FileUploadModule} from 'primeng/fileupload';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';

import {PasswordModule} from 'primeng/password';

// SKITBS components
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {DataProtectionComponent} from './skitbs/base/template/data-protection.component';
import {ContactComponent} from './skitbs/base/template/contact.component';
import {ImprintComponent} from './skitbs/base/template/imprint.component';
import {CopyrightComponent} from './skitbs/base/template/copyright.component';
import {BaseComponent} from './skitbs/base/base.component';
import {BusyComponent} from './skitbs/base/busy/busy.component';
import {Globals} from './app-globals';
import {I18nComponent} from './skitbs/base/utils/i18n.component';
import {SkitbsErrorHandler} from "./skitbs/base/utils/error-handler";
import {EnvPipe} from "./skitbs/base/utils/env.pipe";
import {ServiceWorkerModule} from '@angular/service-worker';
import {RestClient} from "./skitbs/rest-client.service";
import {RestDateInterceptor} from "./skitbs/base/rest/interceptors/rest-date-interceptor";
import {TemplateHeaderComponent} from "./skitbs/base/template/template-header.component";
import {TemplateFooterComponent} from "./skitbs/base/template/template-footer.component";
import {MenubarModule} from "primeng/menubar";
import {OrdersComponent} from "./skitbs/orders/orders.component";
import {HistoryComponent} from "./skitbs/history/history.component";
import {InvoicesComponent} from "./skitbs/invoices/invoices.component";
import {MenuPlansComponent} from "./skitbs/menuplans/menu-plans.component";
import {MessagesComponent} from "./skitbs/messages/messages.component";
import {TemplateComponent} from "./skitbs/base/template/template";
import {InputNumberModule} from "primeng/inputnumber";
import {I18NSelectorComponent} from "./skitbs/base/utils/i18n.selector.component";
import {I18NService} from "./skitbs/base/utils/i18n.service";
import {RestLocaleInterceptor} from "./skitbs/base/rest/interceptors/rest-locale-interceptor";
import {LoginComponent} from "./skitbs/base/template/login.component";
import {JWT_OPTIONS, JwtHelperService} from "@auth0/angular-jwt";
import {AuthService} from "./skitbs/base/auth/auth.service";
import {ProfileComponent} from "./skitbs/profile/profile.component";
import {RestAuthInterceptor} from "./skitbs/base/rest/interceptors/rest-auth-interceptor";
import {UserSwitchComponent} from "./skitbs/base/template/user-switch.component";
import {OrderDetailsComponent} from "./skitbs/orders/order-details.component";
import {OrdersState} from "./skitbs/orders/orders.state";
import {AllergensComponent} from "./skitbs/orders/components/allergens.component";
import {AdditivesComponent} from "./skitbs/orders/components/additives.component";
import {NutritionsComponent} from "./skitbs/orders/components/nutritions.component";
import {ComponentsComponent} from "./skitbs/orders/components/components.component";
import {BusyService} from "./skitbs/base/busy/busy.service";
import {InputTextareaModule} from "primeng/inputtextarea";
import {TooltipModule} from "primeng/tooltip";
import {IosTooltipComponent} from "./skitbs/base/busy/ios-tooltip.component";
import {PrepaidComponent} from "./skitbs/prepaid/prepaid.component";
import {StringFormatPipe} from "./skitbs/base/utils/string-format.pipe";
import { ContractTerminationComponent } from './skitbs/contractTermination/contractTermination.component';
import { DisclaimerComponent } from './skitbs/base/template/disclaimer.component';
import {MatomoTracker, NgxMatomoTrackerModule} from "@ngx-matomo/tracker";
import {environment} from "../environments/environment";
import {NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs";
import {NotificationsComponent} from "./skitbs/notifications/notifications.component";
import {I18nPipe} from "./skitbs/base/utils/i18n.pipe";
import {BirthdayValidatorDirective} from "./skitbs/base/validators/birthday-validator.directive";

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        DropdownModule,
        OverlayPanelModule,
        RippleModule,
        CommonModule,
        DialogModule,
        ReactiveFormsModule,
        AppRoutingModule,
        HttpClientModule,
        PanelModule,
        ToastModule,
        TabViewModule,
        TableModule,
        MenuModule,
        CardModule,
        ButtonModule,
        InputMaskModule,
        DividerModule,
        InputTextModule,
        CalendarModule,
        MessageModule,
        MessagesModule,
        FieldsetModule,
        CheckboxModule,
        FileUploadModule,
        TooltipModule,
        PasswordModule,
        ServiceWorkerModule.register('ngsw-worker.js', {enabled: true /* environment.production */, registrationStrategy: 'registerImmediately'}),
        MenubarModule,
        InputNumberModule,
        InputTextareaModule,
        ConfirmDialogModule,
        NgxMatomoTrackerModule.forRoot({
          disabled: !environment.production,
          scriptUrl: 'https://statistik.fsq-gmbh.de/matomo.js',
          trackers: [
            {trackerUrl: 'https://statistik.fsq-gmbh.de/matomo.php', siteId: environment.MATOMO_SITE_ID}
          ],
        })
    ],
    declarations: [
        AppComponent,
        BaseComponent,
        DataProtectionComponent,
        ContactComponent,
        ImprintComponent,
        DisclaimerComponent,
        CopyrightComponent,
        BusyComponent,
        I18nComponent,
        EnvPipe,
        I18nPipe,
        StringFormatPipe,
        TemplateComponent,
        TemplateHeaderComponent,
        TemplateFooterComponent,
        OrdersComponent,
        NotificationsComponent,
        OrderDetailsComponent,
        HistoryComponent,
        InvoicesComponent,
        ProfileComponent,
        AllergensComponent,
        MenuPlansComponent,
        MessagesComponent,
        I18NSelectorComponent,
        LoginComponent,
        UserSwitchComponent,
        AdditivesComponent,
        NutritionsComponent,
        ComponentsComponent,
        PrepaidComponent,
        IosTooltipComponent,
        ContractTerminationComponent,
        BirthdayValidatorDirective
    ],
    providers: [RestClient, OrdersState, {provide: ErrorHandler, useClass: SkitbsErrorHandler}, ConfirmationService, MessageService, JwtHelperService, AuthService, BusyService,
        {provide: JWT_OPTIONS, useValue: JWT_OPTIONS},
        {provide: HTTP_INTERCEPTORS, useClass: RestDateInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: RestAuthInterceptor, multi: true, deps: [AuthService]},
        {provide: HTTP_INTERCEPTORS, useClass: RestLocaleInterceptor, multi: true, deps: [I18NService]}
    ],
    exports: [
        TemplateComponent
    ],
    bootstrap: [AppComponent]
})
export class AppModule {

    // first time globals/singleton initialisation
    constructor(private globals: Globals, private config: PrimeNGConfig, private matomoTracker: MatomoTracker, private router: Router) {
        config.ripple = true;
        console.log("[AppModule] instance created !");
        this.registerPageTracking();
    }

    registerPageTracking() {
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((value) => {
          this.matomoTracker.trackPageView()
        });
    }
}
